import { motion } from 'framer-motion';
import { FaTrophy, FaSwimmer, FaMedal, FaAward, FaCalendarAlt, FaUsers, FaMapMarkerAlt, FaExclamationCircle } from 'react-icons/fa';
import { GiPodium, GiWhistle } from 'react-icons/gi';
import Seo from '../components/Seo';

function CompetitiveSwimming() {
  const competitions = [
    {
      title: 'Inter-Town Friendly League',
      icon: <FaUsers className="text-4xl" />,
      color: 'blue',
      clubs: 'Ashton, Hyde Seals, Marple, Romiley, Saracens, and Wilmslow',
      description: 'Each club swims home and away galas against every other club. Galas are divided into Junior and Senior sections with trophies for winning clubs.',
      features: [
        'A and B teams allowed',
        'Ages: 9/10, 11/12, 13/14, 15+',
        'Two individual events + one relay OR two relays + one individual',
        'Season-ending Celebration Gala'
      ]
    },
    {
      title: 'Tadpoles Gala',
      icon: <FaSwimmer className="text-4xl" />,
      color: 'green',
      when: 'September - Grand Central Pools, Stockport',
      description: 'Graded age group galas providing excellent opportunities for less experienced swimmers to compete at similar levels.',
      features: [
        'B and C Grade competitions',
        'Awards for top 6 swimmers',
        'Individual and team trophies',
        '"Speeding tickets" for exceeding B Grade times'
      ]
    },
    {
      title: 'Greater Manchester Championships',
      icon: <FaMedal className="text-4xl" />,
      color: 'purple',
      when: 'October - Manchester Aquatic Centre',
      description: 'Age group categories: 12 and under (Junior), 13-14 (Youth), and 15+ (Senior).',
      features: [
        'Awards for 1st, 2nd, 3rd',
        'Trophies for fastest swimmers',
        'Aggregate points system',
        'Club team competitions'
      ]
    },
    {
      title: 'GM Age Group Competition',
      icon: <GiPodium className="text-4xl" />,
      color: 'orange',
      when: 'January/February - Manchester Aquatic Centre',
      description: 'Level 3 Competition licensed by ASA. Entry times must be within set consideration time limits.',
      features: [
        'Age groups: 9/10, 11, 12, 13, 14, 15, 16+',
        'Awards for top 6 finishers',
        'Selection for Inter-Association Competition',
        'Club aggregate points trophies'
      ]
    },
    {
      title: 'Cheshire County Championships',
      icon: <FaTrophy className="text-4xl" />,
      color: 'red',
      when: 'February/March - Macclesfield & Grand Central',
      description: 'Swimmers must achieve appropriate consideration times at Licensed Meets in the 12 months prior.',
      features: [
        'Senior and Age Group competitions',
        'Qualification times required',
        'Entry limits may apply',
        'County-level competition'
      ]
    }
  ];

  const regionalComps = [
    {
      title: 'Age Group Championships',
      when: 'June (2 weekends)',
      location: 'Manchester Aquatic Centre',
      icon: <FaAward className="text-3xl" />
    },
    {
      title: 'Youth Championships',
      when: 'Early May',
      location: 'Liverpool Aquatic Centre',
      icon: <GiWhistle className="text-3xl" />
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: {
        bg: 'from-blue-50 to-blue-100',
        border: 'border-blue-500',
        icon: 'text-blue-600',
        text: 'text-blue-700'
      },
      green: {
        bg: 'from-green-50 to-green-100',
        border: 'border-green-500',
        icon: 'text-green-600',
        text: 'text-green-700'
      },
      purple: {
        bg: 'from-purple-50 to-purple-100',
        border: 'border-purple-500',
        icon: 'text-purple-600',
        text: 'text-purple-700'
      },
      orange: {
        bg: 'from-orange-50 to-orange-100',
        border: 'border-orange-500',
        icon: 'text-orange-600',
        text: 'text-orange-700'
      },
      red: {
        bg: 'from-primary-50 to-primary-100',
        border: 'border-primary-500',
        icon: 'text-primary-600',
        text: 'text-primary-700'
      }
    };
    return colors[color];
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 }
    }
  };

  return (
    <main className="py-16 bg-gradient-to-b from-gray-50 to-white">
      <Seo
        title="Competitive Swimming"
        description="Discover competitive swimming opportunities at Marple Swimming Club, including galas, training squads and pathways for aspiring swimmers."
        path="/competitive"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="font-display text-5xl font-bold text-navy-900 mb-4">Competitive Swimming</h1>
          <p className="text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
            Marple Swimming Club is a member of two local leagues and participates in numerous competitions throughout the year. We encourage all eligible members to represent the Club in external galas and our own Annual Club Gala.
          </p>
          <div className="mt-6 p-4 bg-amber-50 border-2 border-amber-400 rounded-xl max-w-2xl mx-auto flex items-center gap-3 text-left">
            <FaExclamationCircle className="text-amber-600 text-xl flex-shrink-0" />
            <p className="text-gray-800 font-semibold">
              Important: Always adhere to closing dates for entry forms to avoid disappointment!
            </p>
          </div>
        </motion.div>

        {/* Main Competitions */}
        <motion.section
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="mb-16"
        >
          <h2 className="font-display text-4xl font-bold text-navy-900 text-center mb-12">
            Main Competitions
          </h2>
          <div className="space-y-6">
            {competitions.map((comp, index) => {
              const colors = getColorClasses(comp.color);
              return (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className={`bg-gradient-to-br ${colors.bg} border-2 ${colors.border} rounded-2xl p-6 md:p-8 shadow-lg hover:shadow-2xl transition-all duration-300`}
                >
                  <div className="flex flex-col md:flex-row gap-6">
                    {/* Icon */}
                    <div className={`${colors.icon} flex-shrink-0 flex items-start justify-center pt-2`}>
                      {comp.icon}
                    </div>

                    {/* Content */}
                    <div className="flex-1">
                      <h3 className={`text-2xl md:text-3xl font-bold ${colors.text} mb-3`}>
                        {comp.title}
                      </h3>

                      {comp.when && (
                        <div className="flex items-center gap-2 mb-3">
                          <FaCalendarAlt className={colors.icon} />
                          <span className="font-semibold text-gray-800">{comp.when}</span>
                        </div>
                      )}

                      {comp.clubs && (
                        <div className="flex items-start gap-2 mb-3">
                          <FaUsers className={`${colors.icon} mt-1 flex-shrink-0`} />
                          <span className="text-gray-800"><strong>Clubs:</strong> {comp.clubs}</span>
                        </div>
                      )}

                      <p className="text-gray-800 leading-relaxed mb-4">
                        {comp.description}
                      </p>

                      {/* Features */}
                      <div className="grid sm:grid-cols-2 gap-2">
                        {comp.features.map((feature, i) => (
                          <div key={i} className="flex items-start gap-2">
                            <span className={`${colors.text} text-xl flex-shrink-0`}>•</span>
                            <span className="text-gray-700 text-sm">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.section>

        {/* Regional Championships */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h2 className="font-display text-4xl font-bold text-navy-900 text-center mb-8">
            ASA North West Regional Championships
          </h2>
          <p className="text-center text-gray-700 mb-8 max-w-3xl mx-auto">
            Swimmers can only enter if they have achieved the appropriate qualifying time.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            {regionalComps.map((comp, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-6 rounded-2xl shadow-lg border-2 border-primary-200 hover:shadow-2xl transition-all duration-300"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="text-primary-600">
                    {comp.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">{comp.title}</h3>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <FaCalendarAlt className="text-primary-600" />
                    <span className="text-gray-700">{comp.when}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <FaMapMarkerAlt className="text-primary-600" />
                    <span className="text-gray-700">{comp.location}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Club Championships */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-2xl p-8 shadow-lg"
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <FaTrophy className="text-5xl text-primary-600" />
            <h2 className="font-display text-4xl font-bold text-primary-700">Club Championships</h2>
          </div>

          <div className="max-w-3xl mx-auto space-y-4 text-gray-800">
            <p className="leading-relaxed">
              The Club Championships takes place over two Sundays in November each year. Senior and Junior team members are expected to enter all events in their age group.
            </p>

            <div className="grid md:grid-cols-2 gap-4 my-6">
              <div className="bg-white p-4 rounded-xl shadow-md">
                <h4 className="font-bold text-primary-700 mb-2">Who Can Enter?</h4>
                <ul className="space-y-1 text-sm">
                  <li>• Team members: All age group events</li>
                  <li>• Squad 2 members: Open category only</li>
                  <li>• Qualified swimmers: Open category races</li>
                </ul>
              </div>
              <div className="bg-white p-4 rounded-xl shadow-md">
                <h4 className="font-bold text-primary-700 mb-2">Awards</h4>
                <ul className="space-y-1 text-sm">
                  <li>• Top 3 in each age group</li>
                  <li>• Separate Beginners Gala</li>
                  <li>• Presentation Evening trophies</li>
                </ul>
              </div>
            </div>

            <p className="text-sm text-center text-gray-600 italic">
              Entry forms and programme details available a few weeks before competition
            </p>
          </div>
        </motion.section>
      </div>
    </main>
  );
}

export default CompetitiveSwimming;
